import { Injectable, signal } from '@angular/core';
import { DocumentItemPartial } from '@domain';
import type { RaEditorComponent } from 'src/app/shared/components/ra-editor/ra-editor.component';
import { itemTextColor, itemTextSizeRem, ptToRem } from '../../../text-style-options';

/** Onde o texto do campo mora: `value` nos textuais, `description` na mídia e no destaque. */
export type TextTarget = 'value' | 'description';

/** Tamanho (pt) e cor (#rrggbb) ainda não salvos de um campo: a barra mexendo. */
export interface LiveTextStyle {
    fieldId: string;
    /** `undefined` = vale o salvo; `null` = padrão do tipo. */
    textSize?: number | null;
    textColor?: string | null;
}

/**
 * Estado da edição de texto DIRETO na folha (substituiu o composer de rodapé em
 * 2026-09-25, ver docs/PLANO-EDICAO-INLINE.md).
 *
 * Dois campos "atuais", de propósito:
 * - `editingFieldId`: o campo cujo editor inline está em foco. Só ele salva; a base do
 *   compare-and-set (texto + revisão) é do próprio editor, não daqui.
 * - `activeFieldId`: o último campo tocado na folha, alvo da barra de formatação. Não zera
 *   quando o editor perde o foco: clicar em − / + na barra tira o foco do texto e a barra
 *   ainda precisa saber em quem mexer.
 *
 * Quem cria um campo novo (menu, prévia, "criar acima") pede o foco por `requestEdit`; o
 * card do campo foca o editor assim que se vê pedido, mesmo que ainda não exista no DOM.
 */
@Injectable({
    providedIn: 'root',
})
export class TextEditingService {
    public editingFieldId = signal<string | null>(null);
    public activeFieldId = signal<string | null>(null);

    /** Pedido de foco: o card do campo foca o editor quando vê o próprio id aqui. */
    public focusRequest = signal<{ fieldId: string; version: number } | null>(null);

    /** Tamanho e cor ainda não salvos (a barra mexendo): o card mostra por cima da config. */
    public liveStyle = signal<LiveTextStyle | null>(null);

    /** Formatos da seleção no editor em foco (negrito, cor, tamanho, fonte...), para a barra. */
    public selectionFormat = signal<Record<string, unknown>>({});
    /** A seleção cobre um trecho (não é só o cursor): tamanho e fonte agem nele, não no campo. */
    public hasSelection = signal(false);

    private _editor: RaEditorComponent | null = null;
    /** Editor do campo ATIVO (em foco ou não): a barra classifica a linha por ele. */
    private _activeEditor: { fieldId: string; editor: RaEditorComponent } | null = null;
    private _focusVersion = 0;

    /** Editor inline em foco, para a barra aplicar negrito/itálico/cor na seleção. */
    public editor(): RaEditorComponent | null {
        return this.editingFieldId() ? this._editor : null;
    }

    /**
     * Editor do campo ativo, mesmo sem foco (o clique em − / + na barra tira o foco do texto).
     * O Quill guarda a última seleção: um formato de linha aplicado por ele volta ao foco e
     * cai na linha onde o cursor estava.
     */
    public activeEditor(): RaEditorComponent | null {
        const fieldId = this.activeFieldId();
        return fieldId && this._activeEditor?.fieldId === fieldId ? this._activeEditor.editor : null;
    }

    public requestEdit(fieldId: string): void {
        this.activeFieldId.set(fieldId);
        this.focusRequest.set({ fieldId, version: ++this._focusVersion });
    }

    public setActive(fieldId: string | null, editor: RaEditorComponent | null = null): void {
        if (this.activeFieldId() !== fieldId) this.activeFieldId.set(fieldId);
        if (fieldId && editor) this._activeEditor = { fieldId, editor };
    }

    /** O card do campo saiu da tela: o editor dele não serve mais para ninguém. */
    public unregisterEditor(fieldId: string): void {
        if (this._activeEditor?.fieldId === fieldId) this._activeEditor = null;
    }

    /** O editor do campo ganhou foco: vira o alvo de tudo. */
    public beginEditing(fieldId: string, editor: RaEditorComponent | null): void {
        this._editor = editor;
        if (editor) this._activeEditor = { fieldId, editor };
        this.editingFieldId.set(fieldId);
        this.activeFieldId.set(fieldId);
        this.selectionFormat.set({});
        this.hasSelection.set(false);
    }


    /** O editor do campo perdeu o foco. Só encerra se ainda for ele o campo em edição. */
    public endEditing(fieldId: string): void {
        if (this.editingFieldId() !== fieldId) return;

        this._editor = null;
        this.editingFieldId.set(null);
        this.selectionFormat.set({});
        this.hasSelection.set(false);
    }

    /** Estilo ao vivo por campo: para os cards lerem tamanho e cor por cima da config. */
    public sizeRem(field: DocumentItemPartial | null | undefined): string | null {
        if (!field) return null;
        const live = this.liveStyle();
        if (live?.fieldId === field._id && live.textSize !== undefined) {
            return live.textSize === null ? null : ptToRem(live.textSize);
        }
        return itemTextSizeRem(field.configs);
    }

    public color(field: DocumentItemPartial | null | undefined): string | null {
        if (!field) return null;
        const live = this.liveStyle();
        if (live?.fieldId === field._id && live.textColor !== undefined) return live.textColor;
        return itemTextColor(field.configs);
    }

    /** Ao sair da tela ou trocar de seção: nada em edição, nada ativo. */
    public clear(): void {
        this._editor = null;
        this._activeEditor = null;
        this.editingFieldId.set(null);
        this.activeFieldId.set(null);
        this.focusRequest.set(null);
        this.liveStyle.set(null);
        this.selectionFormat.set({});
        this.hasSelection.set(false);
    }
}
